/** Roles, presets and the feature registry (`roles:*`). Shapes copied from workers/api/src/roles. */
import type { Action, BasisPoints, Feature, LeverId, RolePermissions, SensitiveSwitch } from "@helmr/contracts";
import type { ApiClient } from "./client";

export interface RoleRecord {
  id: string; name: string; isOwner: boolean; presetFrom: string | null;
  permissions: Partial<Record<Feature, Action[]>>;
  scope: RolePermissions["scope"];
  sensitive: SensitiveSwitch[];
  /** Effective levers, sorted. The owner's list holds every lever. */
  levers: LeverId[];
  /** Registry version the role was last saved against; `null` before the backfill has run. */
  leverRegistryVersion: number | null;
  discountCapBp: BasisPoints;
  version: number; createdAt: number; updatedAt: number; updatedBy: string | null;
  grantsInvalid?: true;
}
/** `GET /roles/levers` (D57): the registry as the server holds it. The screen renders this, never a bundled copy. */
export interface LeverNode { id: LeverId; label: string; hint: string; requires: LeverId[]; addedIn: number; audited: boolean }
export interface GroupNode { id: string; label: string; levers: LeverNode[] }
export interface LeverTree { version: number; groups: GroupNode[] }
export interface Preset { key: string; name: string; grants: RolePermissions }

export interface RoleBody {
  name?: string; preset?: string; version?: number;
  /** The full lever set, not a diff. */
  levers?: LeverId[];
  scope?: RolePermissions["scope"]; discountCapBp?: number;
}

export const listRoles = (c: ApiClient) => c.get<{ roles: RoleRecord[] }>("/roles").then((r) => r.roles);
export const listPresets = (c: ApiClient) => c.get<{ presets: Preset[] }>("/roles/presets").then((r) => r.presets);
export const getLeverTree = (c: ApiClient) => c.get<LeverTree>("/roles/levers");
export const getRole = (c: ApiClient, id: string) => c.get<{ role: RoleRecord }>(`/roles/${id}`).then((r) => r.role);
export const createRole = (c: ApiClient, body: RoleBody) => c.post<{ role: RoleRecord }>("/roles", body).then((r) => r.role);
/** `version` is required: a stale edit is a 409. */
export const patchRole = (c: ApiClient, id: string, body: RoleBody) => c.patch<{ role: RoleRecord }>(`/roles/${id}`, body).then((r) => r.role);
export const deleteRole = (c: ApiClient, id: string) => c.del<{ ok: true }>(`/roles/${id}`);
